import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Tipos para as tabelas administrativas (NOVAS)
export interface Database {
  public: {
    Tables: {
      // Tabelas administrativas
      admin_users: {
        Row: {
          id: string;
          email: string;
          role: 'superadmin' | 'admin' | 'user';
          region?: string;
          created_at: string;
          updated_at: string;
          last_login: string;
          is_active: boolean;
          is_blocked: boolean;
          permissions: any;
          contact_info: any;
        };
        Insert: {
          id?: string;
          email: string;
          role: 'superadmin' | 'admin' | 'user';
          region?: string;
          created_at?: string;
          updated_at?: string;
          last_login?: string;
          is_active?: boolean;
          is_blocked?: boolean;
          permissions?: any;
          contact_info?: any;
        };
        Update: {
          id?: string;
          email?: string;
          role?: 'superadmin' | 'admin' | 'user';
          region?: string;
          created_at?: string;
          updated_at?: string;
          last_login?: string;
          is_active?: boolean;
          is_blocked?: boolean;
          permissions?: any;
          contact_info?: any;
        };
      };
      
      admin_logs: {
        Row: {
          id: string;
          admin_id: string;
          action: string;
          details: any;
          created_at: string;
          ip_address?: string;
          user_agent?: string;
          severity: 'info' | 'warning' | 'error' | 'critical';
          affected_user_id?: string;
          resource_type?: string;
          resource_id?: string;
        };
        Insert: {
          id?: string;
          admin_id: string;
          action: string;
          details?: any;
          created_at?: string;
          ip_address?: string;
          user_agent?: string;
          severity?: 'info' | 'warning' | 'error' | 'critical';
          affected_user_id?: string;
          resource_type?: string;
          resource_id?: string;
        };
        Update: {
          id?: string;
          admin_id?: string;
          action?: string;
          details?: any;
          created_at?: string;
          ip_address?: string;
          user_agent?: string;
          severity?: 'info' | 'warning' | 'error' | 'critical';
          affected_user_id?: string;
          resource_type?: string;
          resource_id?: string;
        };
      };
      
      admin_metrics: {
        Row: {
          id: string;
          metric_name: string;
          metric_value: number;
          metric_unit?: string;
          recorded_at: string;
          metadata: any;
          category: string;
        };
        Insert: {
          id?: string;
          metric_name: string;
          metric_value: number;
          metric_unit?: string;
          recorded_at?: string;
          metadata?: any;
          category?: string;
        };
        Update: {
          id?: string;
          metric_name?: string;
          metric_value?: number;
          metric_unit?: string;
          recorded_at?: string;
          metadata?: any;
          category?: string;
        };
      };
      
      admin_global_settings: {
        Row: {
          id: string;
          setting_key: string;
          setting_value: any;
          description?: string;
          category: string;
          updated_by?: string;
          updated_at: string;
          is_public: boolean;
        };
        Insert: {
          id?: string;
          setting_key: string;
          setting_value: any;
          description?: string;
          category?: string;
          updated_by?: string;
          updated_at?: string;
          is_public?: boolean;
        };
        Update: {
          id?: string;
          setting_key?: string;
          setting_value?: any;
          description?: string;
          category?: string;
          updated_by?: string;
          updated_at?: string;
          is_public?: boolean;
        };
      };
      
      admin_notifications: {
        Row: {
          id: string;
          admin_id?: string;
          title: string;
          message: string;
          type: 'info' | 'warning' | 'error' | 'success';
          is_read: boolean;
          created_at: string;
          expires_at?: string;
          action_url?: string;
          priority: 'low' | 'normal' | 'high' | 'urgent';
        };
        Insert: {
          id?: string;
          admin_id?: string;
          title: string;
          message: string;
          type?: 'info' | 'warning' | 'error' | 'success';
          is_read?: boolean;
          created_at?: string;
          expires_at?: string;
          action_url?: string;
          priority?: 'low' | 'normal' | 'high' | 'urgent';
        };
        Update: {
          id?: string;
          admin_id?: string;
          title?: string;
          message?: string;
          type?: 'info' | 'warning' | 'error' | 'success';
          is_read?: boolean;
          created_at?: string;
          expires_at?: string;
          action_url?: string;
          priority?: 'low' | 'normal' | 'high' | 'urgent';
        };
      };

      // Tabela de planos
      plans: {
        Row: {
          id: string;
          name: string;
          slug: string;
          description?: string;
          features: any;
          price_monthly: number;
          price_yearly: number;
          is_active: boolean;
          created_at: string;
          updated_at: string;
          metadata: any;
        };
        Insert: {
          id?: string;
          name: string;
          slug: string;
          description?: string;
          features?: any;
          price_monthly?: number;
          price_yearly?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
          metadata?: any;
        };
        Update: {
          id?: string;
          name?: string;
          slug?: string;
          description?: string;
          features?: any;
          price_monthly?: number;
          price_yearly?: number;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
          metadata?: any;
        };
      };

      // Tabela de configurações de usuários da extensão
      extension_user_settings: {
        Row: {
          id: string;
          user_id: string;
          plan_id: string;
          is_blocked: boolean;
          blocked_reason?: string;
          blocked_at?: string;
          blocked_by?: string;
          created_at: string;
          updated_at: string;
          metadata: any;
        };
        Insert: {
          id?: string;
          user_id: string;
          plan_id: string;
          is_blocked?: boolean;
          blocked_reason?: string;
          blocked_at?: string;
          blocked_by?: string;
          created_at?: string;
          updated_at?: string;
          metadata?: any;
        };
        Update: {
          id?: string;
          user_id?: string;
          plan_id?: string;
          is_blocked?: boolean;
          blocked_reason?: string;
          blocked_at?: string;
          blocked_by?: string;
          created_at?: string;
          updated_at?: string;
          metadata?: any;
        };
      };
    };
    
    Views: {
      extension_user_stats: {
        Row: {
          user_id: string;
          email: string;
          created_at: string;
          last_sign_in_at?: string;
          raw_user_meta_data: any;
          plan_name: string;
          plan_slug: string;
          plan_id: string;
          is_blocked: boolean;
          blocked_reason?: string;
          blocked_at?: string;
          profiles_saved: number;
          labels_created: number;
          notes_added: number;
          company_reviews: number;
          last_activity?: string;
          activity_status: 'active' | 'inactive' | 'abandoned';
        };
      };
      
      linkedin_profile_stats: {
        Row: {
          id: string;
          slug: string;
          name: string;
          avatar?: string;
          created_at: string;
          updated_at: string;
          saved_by_users: number;
          common_labels: string[];
          last_saved_at?: string;
        };
      };
      
      label_usage_stats: {
        Row: {
          id: string;
          name: string;
          color: string;
          created_at: string;
          updated_at: string;
          profiles_count: number;
          users_count: number;
          last_used_at?: string;
        };
      };

      company_stats: {
        Row: {
          id: string;
          company_slug: string;
          company_name: string;
          company_url?: string;
          company_avatar?: string;
          created_at: string;
          updated_at: string;
          total_reviews: number;
          avg_overall_rating: number;
          recommendation_rate: number;
          last_review_date?: string;
          five_star_reviews: number;
          four_star_reviews: number;
          three_star_reviews: number;
          two_star_reviews: number;
          one_star_reviews: number;
        };
      };
    };
    
    Functions: {
      log_admin_action: {
        Args: {
          p_admin_id: string;
          p_action: string;
          p_details?: any;
          p_severity?: string;
          p_affected_user_id?: string;
          p_resource_type?: string;
          p_resource_id?: string;
        };
        Returns: string;
      };
      
      get_system_stats: {
        Args: {};
        Returns: any;
      };

      assign_plan_to_user: {
        Args: {
          p_user_id: string;
          p_plan_slug: string;
          p_admin_id?: string;
        };
        Returns: string;
      };

      toggle_user_block: {
        Args: {
          p_user_id: string;
          p_is_blocked: boolean;
          p_reason?: string;
          p_admin_id?: string;
        };
        Returns: string;
      };
    };
  };
}
